import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet';

const getStandardStructureddData = () => {
    const data = {
        '@context': 'https://schema.org/',
        '@type': 'WebSite',
        name: 'SaleScreen',
        url: 'https://www.salesscreen.com',
        potentialAction: {
            '@type': 'SearchAction',
            target: '{search_term_string}',
            'query-input': 'required name=search_term_string'
        }
    };

    return JSON.stringify(data);
};

const getPeopleStructuredData = (name) => {
    switch (name) {
        case 'sindre':
            return JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Person',
                name: 'Sindre Haaland',
                url: 'https://www.salesscreen.com/about',
                image: 'https://cdn.sanity.io/images/ku7s4y42/production/88ed7d0f9c44041ffb272a91ddd6e707fd7c06ba-600x600.jpg?w=320&h=320&auto=format',
                sameAs: 'https://www.linkedin.com/in/shaaland/',
                jobTitle: 'CEO',
                worksFor: {
                    '@type': 'Organization',
                    name: 'SalesScreen'
                }
            });
        case 'antoine':
            return JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Person',
                name: 'Antoine Thébault',
                url: 'https://www.salesscreen.com/about',
                image: 'https://cdn.sanity.io/images/ku7s4y42/production/0e5298cebec809c710eea6cf7e10d99cbbb7e810-600x600.jpg?w=320&h=320&auto=format',
                sameAs: 'https://www.linkedin.com/in/antoine-thebault-629b1224/',
                jobTitle: 'CFO',
                worksFor: {
                    '@type': 'Organization',
                    name: 'SalesScreen'
                }
            });
        case 'oystein':
            return JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Person',
                name: 'Øystein Heimark',
                url: 'https://www.salesscreen.com/about',
                image: 'https://cdn.sanity.io/images/ku7s4y42/production/fb4e968f9296f45c80290c8628c9ae52a17f53c9-600x600.jpg?w=320&h=320&auto=format',
                sameAs: 'https://www.linkedin.com/in/oysteinheimark/',
                jobTitle: 'CTO',
                worksFor: {
                    '@type': 'Organization',
                    name: 'SalesScreen'
                }
            });
        case 'marius':
            return JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Person',
                name: 'Marius Ekerholt',
                url: 'https://www.salesscreen.com/about',
                image: 'https://cdn.sanity.io/images/ku7s4y42/production/1dc0ed43aa9748612f79021c28f23c10551dcac6-600x600.jpg?w=320&h=320&auto=format',
                sameAs: 'https://www.linkedin.com/in/marius-ekerholt-75406953/',
                jobTitle: 'CPO',
                worksFor: {
                    '@type': 'Organization',
                    name: 'SalesScreen'
                }
            });
        default:
            return JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Person',
                name: 'Sindre Haaland',
                url: 'https://www.salesscreen.com/about',
                image: 'https://cdn.sanity.io/images/ku7s4y42/production/88ed7d0f9c44041ffb272a91ddd6e707fd7c06ba-600x600.jpg?w=320&h=320&auto=format',
                sameAs: 'https://www.linkedin.com/in/shaaland/',
                jobTitle: 'CEO',
                worksFor: {
                    '@type': 'Organization',
                    name: 'SalesScreen'
                }
            });
    }
};

const getOrganizationStructuredData = () => {
    const data = {
        '@context': 'https://schema.org',
        '@type': 'Corporation',
        name: 'SalesScreen',
        alternateName: 'Sales Screen',
        url: 'https://www.salesscreen.com',
        logo: '',
        sameAs: [
            'https://www.facebook.com/salesscreen',
            'https://twitter.com/salesscreen?lang=en',
            'https://www.instagram.com/salesscreen/',
            'https://www.linkedin.com/company/salesscreen',
            'https://www.salesscreen.com'
        ]
    };

    return JSON.stringify(data);
};

const getArticleStructuredData = (articleData) => {
    const { headline, imageUrl, date, blogAuthors, authorName } = articleData;
    const data = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: headline,
        image: [imageUrl],
        datePublished: date,
        dateModified: date,
        author:
            blogAuthors && blogAuthors.itemList.length
                ? blogAuthors.itemList.map((blogAuthor) => {
                      return {
                          '@type': 'Person',
                          name: blogAuthor.name,
                          url: `https://www.salesscreen.com/blog/authors/${blogAuthor.slug.current}`
                      };
                  })
                : [
                      {
                          '@type': 'Person',
                          name: authorName
                      }
                  ],
        publisher: {
            '@type': 'Organization',
            name: 'SalesScreen',
            logo: {
                '@type': 'ImageObject',
                url: 'https://content.salesscreen.com/common/logos/standard-horizontal.png'
            }
        }
    };

    return JSON.stringify(data);
};

function Seo({
    description,
    lang,
    title,
    img,
    addOrganizationInfo,
    noindex,
    addArticleStructuredData,
    articleData
}) {
    const metaImage = img
        ? img
        : 'https://content.salesscreen.com/hermes-cms/images/campaign/salesscreen-love-the-game.png';

    const openGraphMetaProperties = [
        {
            property: `og:type`,
            content: 'website'
        },
        {
            property: `og:site_name`,
            content: 'SalesScreen'
        },
        {
            property: `og:title`,
            content: title
        },
        {
            property: `og:description`,
            content: description
        },
        {
            property: `og:image`,
            content: metaImage
        }
    ];

    const twitterMetaProperties = [
        {
            property: `twitter:card`,
            content: 'summary'
        },
        {
            property: `twitter:site`,
            content: '@salesscreen'
        },
        {
            property: `twitter:title`,
            content: title
        },
        {
            property: `twitter:description`,
            content: description
        },
        {
            property: `twitter:image`,
            content: metaImage
        }
    ];

    const baseMetas = [
        {
            name: `author`,
            content: 'SalesScreen'
        },
        {
            name: `description`,
            content: description
        },
        {
            name: `google-site-verification`,
            content: 'CGgO6Ot89MEDZMnav2h5geQlef_VBAesAKDnYni-tsc'
        },
        {
            name: `facebook-domain-verification`,
            content: 'psdlui0judu0evgksuqxx22hppor21'
        }
    ];

    if (noindex) {
        baseMetas.push({
            name: `robots`,
            content: 'noindex'
        });
    }

    return (
        <Helmet
            htmlAttributes={{
                lang
            }}
            title={title}
            meta={baseMetas.concat(openGraphMetaProperties).concat(twitterMetaProperties)}
        >
            <script
                id="osano"
                src="https://cmp.osano.com/16CJQjUYi0AGGFaPb/992da355-5596-4eef-871b-dce4fe96436e/osano.js"
            />
            <link
                id="favicon"
                rel="shortcut icon"
                href="https://content.salesscreen.com/hermes-cms/images/logos/metaIcons/favicon.ico"
            />
            <link
                rel="icon"
                type="image/png"
                sizes="32x32"
                href="https://content.salesscreen.com/hermes-cms/images/logos/metaIcons/favicon-32x32.png"
            />
            <link
                rel="icon"
                type="image/png"
                sizes="16x16"
                href="https://content.salesscreen.com/hermes-cms/images/logos/metaIcons/favicon-16x16.png"
            />
            <script type="application/ld+json">{getStandardStructureddData()}</script>
            {addOrganizationInfo ? (
                <script type="application/ld+json">{getOrganizationStructuredData()}</script>
            ) : null}
            {addOrganizationInfo ? (
                <script type="application/ld+json">{getPeopleStructuredData('sindre')}</script>
            ) : null}
            {addOrganizationInfo ? (
                <script type="application/ld+json">{getPeopleStructuredData('antoine')}</script>
            ) : null}
            {addOrganizationInfo ? (
                <script type="application/ld+json">{getPeopleStructuredData('oystein')}</script>
            ) : null}
            {addOrganizationInfo ? (
                <script type="application/ld+json">{getPeopleStructuredData('marius')}</script>
            ) : null}

            {addArticleStructuredData ? (
                <script type="application/ld+json">{getArticleStructuredData(articleData)}</script>
            ) : null}
        </Helmet>
    );
}
Seo.defaultProps = {
    lang: `en`,
    meta: [],
    description: ``
};
Seo.propTypes = {
    description: PropTypes.string,
    lang: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string.isRequired
};
export default Seo;
